import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const FeaturesShowcase = () => {
  return (
    <section id="features" className="py-16 sm:py-24 lg:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section header */}
        <div className="max-w-2xl mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-4 sm:mb-5">
            <Icon name="Layers" size={13} />
            <span>Everything in one platform</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-gray-900 leading-tight mb-3 sm:mb-4">
            Every tool you need to own your forms
          </h2>
          <p className="text-base sm:text-lg text-gray-500 leading-relaxed">
            Stop duct-taping five different tools together. WebForms.biz gives you a complete command center for every form you'll ever build.
          </p>
        </div>

        {/* Bento grid — stacked on mobile, grid on md+ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">

          {/* Card 1 — Large: Form Builder */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8 pb-0">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs font-semibold text-teal-700 mb-4 sm:mb-5">
                <Icon name="MousePointerClick" size={13} />
                <span>Form Builder</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
                Drag-and-drop. Done in minutes.
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4 sm:mb-6 max-w-lg">
                50+ field types, conditional logic, multi-step flows, per-field styling, and a global theme builder. Build any form you can imagine — no code, no friction.
              </p>
              <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                {['50+ field types', 'Conditional logic', 'Multi-step forms', 'Global themes', 'Live preview']?.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs font-medium text-gray-600">{tag}</span>
                ))}
              </div>
            </div>
            {/* Visual */}
            <div className="mx-4 sm:mx-8 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-t-xl border border-b-0 border-gray-100 p-4 sm:p-5">
              <div className="space-y-2.5">
                {[
                  { label: 'Full Name', type: 'text', active: false },
                  { label: 'Email Address', type: 'email', active: true },
                  { label: 'Phone Number', type: 'tel', active: false },
                ]?.map(({ label, active }) => (
                  <div key={label} className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${active ? 'border-teal-400 bg-white shadow-sm' : 'border-gray-200 bg-white/60'}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${active ? 'bg-teal-500' : 'bg-gray-300'}`} />
                    <span className={active ? 'text-gray-800 font-medium' : 'text-gray-400'}>{label}</span>
                    {active && <Icon name="GripVertical" size={14} className="ml-auto text-gray-300" />}
                  </div>
                ))}
                <div className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-teal-300 rounded-xl text-xs text-teal-600 font-medium">
                  <Icon name="Plus" size={14} />
                  <span>Drop a field here</span>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2 — Analytics */}
          <div className="bg-[#0a1628] rounded-2xl border border-white/5 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-500/15 border border-blue-500/20 rounded-full text-xs font-semibold text-blue-400 mb-4 sm:mb-5">
                <Icon name="BarChart3" size={13} />
                <span>Analytics</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-2 sm:mb-3">
                See what's working — in real time
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4 sm:mb-6">
                Conversion funnels, drop-off analysis, device breakdown, and geographic heatmaps. Know exactly where users abandon your forms.
              </p>
              {/* Mini chart */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-400 font-medium">Conversion Rate</span>
                  <span className="text-xs text-green-400 font-semibold flex items-center gap-1">
                    <Icon name="TrendingUp" size={11} /> +12%
                  </span>
                </div>
                <div className="flex items-end gap-1 h-16">
                  {[45, 60, 52, 70, 65, 80, 75, 88, 82, 95]?.map((h, i) => (
                    <div key={i} className="flex-1 rounded-sm transition-all"
                      style={{ height: `${h}%`, background: i >= 8 ? '#0e7572' : `rgba(14,117,114,${0.15 + i * 0.07})` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Card 3 — Payments */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-full text-xs font-semibold text-green-700 mb-4 sm:mb-5">
                <Icon name="CreditCard" size={13} />
                <span>Payments</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
                Collect payments inside your forms
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4 sm:mb-5">
                Stripe-powered checkout built right in. One-time payments, subscriptions, and invoicing — no redirect, no friction.
              </p>
              <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-4 border border-green-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-500">Order Total</span>
                  <span className="text-lg font-bold text-gray-900 font-heading">$149.00</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 text-xs text-gray-500 mb-3">
                  <Icon name="CreditCard" size={13} className="text-gray-400" />
                  <span>•••• •••• •••• 4242</span>
                  <span className="ml-auto text-gray-400">Visa</span>
                </div>
                <div className="w-full py-2.5 bg-[#0e7572] rounded-lg text-xs text-white font-semibold text-center touch-manipulation">
                  Pay securely →
                </div>
              </div>
            </div>
          </div>

          {/* Card 5 — Integrations */}
          <div className="bg-gradient-to-br from-[#0a1628] to-[#0e2040] rounded-2xl border border-white/5 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500/15 border border-yellow-500/20 rounded-full text-xs font-semibold text-yellow-400 mb-4 sm:mb-5">
                <Icon name="Zap" size={13} />
                <span>Integrations</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-2 sm:mb-3">
                 Connect with your existing tools
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4 sm:mb-6">
                Native Zapier integration, custom webhooks, and a full REST API. Your form data flows exactly where you need it.
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { name: 'Zapier', icon: 'Zap', color: 'text-orange-400', bg: 'bg-orange-500/10' },
                  { name: 'Stripe', icon: 'CreditCard', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                  { name: 'Slack', icon: 'MessageSquare', color: 'text-purple-400', bg: 'bg-purple-500/10' },
                  { name: 'HubSpot', icon: 'BarChart2', color: 'text-orange-300', bg: 'bg-orange-500/10' },
                  { name: 'REST API', icon: 'Code2', color: 'text-teal-400', bg: 'bg-teal-500/10' },
                  { name: 'Webhooks', icon: 'Globe', color: 'text-green-400', bg: 'bg-green-500/10' },
                ]?.map(({ name, icon, color, bg }) => (
                  <div key={name} className={`flex flex-col items-center gap-1.5 p-3 ${bg} rounded-xl border border-white/5 touch-manipulation`}>
                    <Icon name={icon} size={18} className={color} />
                    <span className="text-xs text-gray-400 font-medium text-center">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 6 — A/B Testing */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs font-semibold text-purple-700 mb-4 sm:mb-5">
                <Icon name="TestTube2" size={13} />
                <span>A/B Testing</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
                Test. Learn. Convert more.
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4 sm:mb-5">
                Run split tests on any form variant. Statistical confidence scoring tells you exactly when to pick a winner.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-100 text-center">
                  <div className="text-lg font-bold text-purple-700 font-heading">62%</div>
                  <div className="text-xs text-purple-500 font-medium">Variant A</div>
                  <div className="mt-1.5 h-1.5 bg-purple-200 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-500 rounded-full" style={{ width: '62%' }} />
                  </div>
                </div>
                <div className="p-3 bg-teal-50 rounded-xl border border-teal-100 text-center">
                  <div className="text-lg font-bold text-teal-700 font-heading">78%</div>
                  <div className="text-xs text-teal-500 font-medium flex items-center justify-center gap-1">
                    Variant B <Icon name="Trophy" size={11} className="text-teal-600" />
                  </div>
                  <div className="mt-1.5 h-1.5 bg-teal-200 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full" style={{ width: '78%' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Card 7 — White Label */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-fuchsia-50 border border-fuchsia-200 rounded-full text-xs font-semibold text-fuchsia-700 mb-4 sm:mb-5">
                <Icon name="Palette" size={13} />
                <span>White Label</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-gray-900 mb-2 sm:mb-3">
                Your brand. Your clients. Zero trace of us.
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4 sm:mb-5">
                Custom domain, your logo, your colors, your email sender. Resell to clients who'll never know we exist.
              </p>
              <div className="space-y-2.5">
                {[
                  { icon: 'Globe', text: 'Custom domain support' },
                  { icon: 'Palette', text: 'Full brand color control' },
                  { icon: 'Mail', text: 'Custom email sender name' },
                  { icon: 'Users', text: 'White-label client portals' },
                ]?.map(({ icon, text }) => (
                  <div key={text} className="flex items-center gap-2.5 text-sm text-gray-600">
                    <Icon name={icon} size={14} className="text-fuchsia-500 flex-shrink-0" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Card 8 — Submissions */}
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-white/5 overflow-hidden shadow-sm hover:shadow-md active:shadow-sm transition-all duration-300 group touch-manipulation">
            <div className="p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-500/15 border border-rose-500/20 rounded-full text-xs font-semibold text-rose-400 mb-4 sm:mb-5">
                <Icon name="Inbox" size={13} />
                <span>Submissions</span>
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-bold text-white mb-2 sm:mb-3">
                Every response, organized and searchable
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-4 sm:mb-5">
                Filter, label, export, and bulk-manage every submission. Full detail view with file attachments and payment records.
              </p>
              <div className="space-y-2">
                {[
                  { name: 'John Smith', time: '2 min ago', status: 'New', color: 'bg-teal-500' },
                  { name: 'Sarah Chen', time: '14 min ago', status: 'Reviewed', color: 'bg-blue-500' },
                  { name: 'Mike Johnson', time: '1 hr ago', status: 'Archived', color: 'bg-gray-500' },
                ]?.map(({ name, time, status, color }) => (
                  <div key={name} className="flex items-center gap-3 px-3 py-2.5 bg-white/5 rounded-xl border border-white/5">
                    <div className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />
                    <span className="text-sm text-gray-300 font-medium flex-1 truncate">{name}</span>
                    <span className="text-xs text-gray-500 flex-shrink-0">{time}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color} bg-opacity-20 text-white flex-shrink-0`}>{status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-10 sm:mt-12">
          <Link
            to="/authentication"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-[#0e7572] hover:bg-[#0a5f5c] active:bg-[#094f4c] text-white font-semibold text-sm sm:text-base rounded-xl transition-all duration-200 shadow-lg shadow-teal-100 hover:shadow-xl hover:-translate-y-0.5 touch-manipulation"
          >
            <span>Explore all features</span>
            <Icon name="ArrowRight" size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturesShowcase;